import { useState } from "react"

import type { DatetimePickerData, UIEvent } from "../types"
import { CardActions, CardButton, CardShell } from "./card_shell"
import { SelectInput, type SelectInputOption } from "./select_input"
import styles from "./datetime_picker_card.module.scss"

interface Props {
  ui: UIEvent
  onRespond: (uiId: string, actionId: string, value: unknown, displayText?: string) => void
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

function hourLabel(hour: number): string {
  const period = hour < 12 ? "AM" : "PM"
  const display = hour % 12 === 0 ? 12 : hour % 12
  return `${display} ${period}`
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

const MONTH_OPTIONS: SelectInputOption[] = MONTHS.map((label, index) => ({
  value: String(index + 1).padStart(2, "0"),
  label,
}))

const HOUR_OPTIONS: SelectInputOption[] = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour).padStart(2, "0"),
  label: hourLabel(hour),
}))

const ALL_MINUTE_OPTIONS: SelectInputOption[] = Array.from({ length: 60 }, (_, minute) => {
  const value = String(minute).padStart(2, "0")
  return { value, label: `${value} Minutes` }
})

const DatetimePickerCard = ({ ui, onRespond }: Props) => {
  const { prompt, mode, minute_options: minuteOptions } = ui.data as DatetimePickerData
  const noYear = mode === "no_year_datetime"
  const currentYear = new Date().getFullYear()

  const yearOptions: SelectInputOption[] = Array.from({ length: 6 }, (_, offset) => {
    const value = String(currentYear + offset)
    return { value, label: value }
  })
  const minuteSelectOptions = noYear
    ? (minuteOptions ?? []).map(value => ({ value, label: `${value} Minutes` }))
    : ALL_MINUTE_OPTIONS

  const [step, setStep] = useState<"date" | "time">("date")
  const [answered, setAnswered] = useState(false)
  const [year, setYear] = useState(String(currentYear))
  const [month, setMonth] = useState("01")
  const [day, setDay] = useState("01")
  const [hour, setHour] = useState("00")
  const [minute, setMinute] = useState(minuteSelectOptions[0].value)

  const effectiveYear = noYear ? currentYear : Number(year)
  const dayOptions: SelectInputOption[] = Array.from(
    { length: daysInMonth(effectiveYear, Number(month)) },
    (_, index) => {
      const value = String(index + 1).padStart(2, "0")
      return { value, label: String(index + 1) }
    }
  )

  const submit = () => {
    if (answered) return
    setAnswered(true)
    const safeDay = String(Math.min(Number(day), dayOptions.length)).padStart(2, "0")
    const value = `${effectiveYear}-${month}-${safeDay}T${hour}:${minute}`
    const [hourPart, period] = hourLabel(Number(hour)).split(" ")
    const date = `${MONTHS[Number(month) - 1]} ${Number(safeDay)}`
    const time = `${hourPart}:${minute} ${period}`
    const displayText = noYear ? `${date} at ${time}` : `${date}, ${effectiveYear} at ${time}`
    onRespond(ui.id, "select", value, displayText)
  }

  const header = step === "date" ? prompt : "What time would you like to be notified?"

  return (
    <CardShell prompt={header} onDismiss={() => onRespond(ui.id, "close", undefined)}>
      {step === "date" ? (
        <>
          <div className={styles["controls"]}>
            <SelectInput ariaLabel="Month" value={month} options={MONTH_OPTIONS} onChange={setMonth} />
            <SelectInput ariaLabel="Day" value={day} options={dayOptions} onChange={setDay} />
            {noYear ? null : <SelectInput ariaLabel="Year" value={year} options={yearOptions} onChange={setYear} />}
          </div>
          <CardActions>
            <CardButton variant="primary" onClick={() => setStep("time")}>
              Next
            </CardButton>
          </CardActions>
        </>
      ) : (
        <>
          <div className={styles["controls"]}>
            <SelectInput ariaLabel="Hour" value={hour} options={HOUR_OPTIONS} onChange={setHour} disabled={answered} />
            <SelectInput
              ariaLabel="Minute"
              value={minute}
              options={minuteSelectOptions}
              onChange={setMinute}
              disabled={answered}
            />
          </div>
          <CardActions>
            <CardButton variant="secondary" disabled={answered} onClick={() => setStep("date")}>
              Go back
            </CardButton>
            <CardButton variant="primary" disabled={answered} onClick={submit}>
              Select
            </CardButton>
          </CardActions>
        </>
      )}
    </CardShell>
  )
}

export default DatetimePickerCard
